import React, { useState } from 'react';
import type { MouseEvent } from 'react';
import { KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';
import { Button, Menu, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router';
import { taskTableStyles } from '../taskUi.tsx';
import { tasksPageText } from '../constants.ts';
import { tasksPageStyles } from '../styles.ts';
import type { ITaskRow } from '../types.ts';
import { getGuaranteesTaskAwaitingStatus, getGuaranteesTaskInProgressStatus } from '../utils.ts';

interface ITaskActionsMenuProps {
  actionPath: string;
  activeGuaranteesActionId?: string | null;
  canAssignGuaranteesTask?: boolean;
  id: string;
  onGuaranteesAssignTask?: (row: ITaskRow) => void;
  onGuaranteesRefuseTask?: (row: ITaskRow) => void;
  onGuaranteesTakeInWork?: (row: ITaskRow) => void;
  row: ITaskRow;
}

const TaskActionsMenu: React.FC<ITaskActionsMenuProps> = ({
  actionPath,
  activeGuaranteesActionId,
  canAssignGuaranteesTask = false,
  id,
  onGuaranteesAssignTask,
  onGuaranteesRefuseTask,
  onGuaranteesTakeInWork,
  row,
}) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);
  const isGuaranteesRow = !!row.guaranteeStatus;
  const hasViewAction = !isGuaranteesRow || row.canViewApplication === true;
  const hasGuaranteeTakeInWorkAction =
    isGuaranteesRow &&
    typeof row.version === 'number' &&
    !!getGuaranteesTaskInProgressStatus(row.guaranteeStatus) &&
    row.isAssignedToCurrentUser === true &&
    row.canViewApplication !== true &&
    !!onGuaranteesTakeInWork;
  const hasGuaranteeRefuseTaskAction =
    isGuaranteesRow &&
    typeof row.version === 'number' &&
    !!getGuaranteesTaskAwaitingStatus(row.guaranteeStatus) &&
    !!onGuaranteesRefuseTask;
  const hasGuaranteeAssignTaskAction =
    isGuaranteesRow &&
    typeof row.version === 'number' &&
    !!row.assigneeUnit &&
    canAssignGuaranteesTask &&
    !!onGuaranteesAssignTask;
  const isGuaranteesActionInProgress = activeGuaranteesActionId === row.id;
  const isGuaranteeTakeInWorkActionDisabled = isGuaranteesActionInProgress;
  const isGuaranteeStatusActionDisabled =
    isGuaranteesActionInProgress || !!row.internalApprovalType;

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleView = () => {
    handleClose();
    navigate(actionPath);
  };

  const handleTakeInWork = () => {
    handleClose();
    onGuaranteesTakeInWork?.(row);
  };

  const handleRefuseTask = () => {
    handleClose();
    onGuaranteesRefuseTask?.(row);
  };

  const handleAssignTask = () => {
    handleClose();
    onGuaranteesAssignTask?.(row);
  };

  return (
    <>
      <Button
        variant="contained"
        size="small"
        endIcon={<KeyboardArrowDownIcon fontSize="small" />}
        onClick={handleOpen}
        sx={taskTableStyles.tableActionBtn}
        aria-controls={isOpen ? `task-actions-menu-${id}` : undefined}
        aria-haspopup="true"
        aria-expanded={isOpen ? 'true' : undefined}
      >
        {tasksPageText.actionButton}
      </Button>
      <Menu
        id={`task-actions-menu-${id}`}
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        keepMounted
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        slotProps={{
          paper: {
            sx: tasksPageStyles.taskActionsMenuPaper,
          },
        }}
      >
        {hasViewAction && (
          <MenuItem onClick={handleView} sx={tasksPageStyles.taskActionsMenuItem}>
            {tasksPageText.viewActionLabel}
          </MenuItem>
        )}
        {hasGuaranteeAssignTaskAction && (
          <MenuItem
            disabled={isGuaranteesActionInProgress}
            onClick={handleAssignTask}
            sx={tasksPageStyles.taskActionsMenuItem}
          >
            {tasksPageText.assignExecutorActionLabel}
          </MenuItem>
        )}
        {hasGuaranteeTakeInWorkAction && (
          <MenuItem
            disabled={isGuaranteeTakeInWorkActionDisabled}
            onClick={handleTakeInWork}
            sx={tasksPageStyles.taskActionsMenuItem}
          >
            {isGuaranteesActionInProgress
              ? tasksPageText.actionInProgressLabel
              : tasksPageText.takeInWorkActionLabel}
          </MenuItem>
        )}
        {hasGuaranteeRefuseTaskAction && (
          <MenuItem
            disabled={isGuaranteeStatusActionDisabled}
            onClick={handleRefuseTask}
            sx={tasksPageStyles.taskActionsMenuItem}
          >
            {isGuaranteesActionInProgress
              ? tasksPageText.actionInProgressLabel
              : tasksPageText.refuseTaskActionLabel}
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default TaskActionsMenu;
